import type { CardCapture } from "./contracts";
import {
  VGS_COLLECT_SCRIPT_INTEGRITY,
  VGS_COLLECT_SCRIPT_URL,
} from "./collect-script";
export {
  VGS_COLLECT_SCRIPT_INTEGRITY,
  VGS_COLLECT_SCRIPT_URL,
} from "./collect-script";

/**
 * How inline fields look, so a checkout can dress them in its own tokens:
 * the one-line dark group by default, or `split` — the card number on its
 * own line and the expiry and code side by side under it, each its own
 * field, as the checkout's own card fields are drawn. Values are written
 * into the capture document, so anything but plain CSS values is refused
 * (the default is kept).
 */
export type CaptureAppearance = {
  layout: "row" | "split";
  text: string;
  placeholder: string;
  background: string;
  border: string;
  focusBorder: string;
  focusShadow: string;
  danger: string;
  radius: number;
  fontFamily: string;
  fontSize: number;
  /** One field's height, in px. */
  height: number;
  /** Between the split layout's lines and fields, in px. */
  gap: number;
  /** Split layout only: room kept free at the card number's trailing end,
   *  in px, beyond the field's own padding, for a control the host draws
   *  over it (the checkout's saved-card chip or save tick). The number
   *  scrolls within what is left; it never runs under the control. */
  numberTrailingPadding: number;
};

export const DEFAULT_CAPTURE_APPEARANCE: CaptureAppearance = {
  layout: "row",
  text: "#fff",
  placeholder: "#b6c2d9",
  background: "#ffffff0c",
  border: "#ffffff50",
  focusBorder: "#b9d6ff",
  focusShadow: "none",
  danger: "#ffc4c4",
  radius: 10,
  fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  fontSize: 16,
  height: 52,
  gap: 8,
  numberTrailingPadding: 0,
};

// Slash is needed for CSS Color 4 alpha (rgb(0 0 0 / 87%)); hex digits
// already admit #RRGGBBAA. The delimiter and URL guards below still apply.
const CSS_VALUE = new RegExp("^[#\\w \\t(),.%+/-]+$(?![\\s\\S])");
const FONT_FAMILY = /^[\w \t,"-]+$(?![\s\S])/;
const UNSAFE_CSS_VALUE = /(?:url|expression)\s*\(|@import/i;
/** A plain CSS value, or the default: nothing that could end the style,
 *  the script string or the document around it. */
export function captureAppearance(
  given: Partial<CaptureAppearance> | undefined,
): CaptureAppearance {
  const out = { ...DEFAULT_CAPTURE_APPEARANCE };
  if (!given) return out;
  for (const key of Object.keys(out) as Array<keyof CaptureAppearance>) {
    const value = given[key];
    if (value === undefined) continue;
    if (typeof out[key] === "number") {
      if (
        typeof value === "number" &&
        Number.isFinite(value) &&
        (key === "height"
          ? value > 2
          : key === "fontSize"
            ? value > 0
            : value >= 0) &&
        value <= 200
      )
        (out as Record<string, unknown>)[key] = value;
    } else if (key === "layout") {
      if (value === "row" || value === "split") out.layout = value;
    } else if (
      typeof value === "string" &&
      value.length <= 200 &&
      !UNSAFE_CSS_VALUE.test(value) &&
      (key === "fontFamily" ? FONT_FAMILY : CSS_VALUE).test(value)
    )
      (out as Record<string, unknown>)[key] = value;
  }
  return out;
}

/** How tall the inline fields are for an appearance: one line for a code
 *  alone or the one-line group, two for a split new card. */
export function captureHeight(
  appearance: Partial<CaptureAppearance> | undefined,
  refresh: boolean,
) {
  const a = captureAppearance(appearance);
  return a.layout === "split" && !refresh ? a.height * 2 + a.gap : a.height;
}

/** The host's submit to an inline document drawn for `drawn`: the aliases are
 *  made with `target`'s token and confirmed under its id. Another capture
 *  than the drawn one (a renewal: `capture-renewal.ts`) travels with the
 *  submit; the document and what was typed in it stay. */
export function captureSubmitMessage(
  drawn: Pick<CardCapture, "id">,
  target?: Pick<CardCapture, "id" | "accessToken">,
) {
  return JSON.stringify({
    type: "lissen-card-submit",
    captureId: drawn.id,
    ...(target && target.id !== drawn.id
      ? { capture: { id: target.id, accessToken: target.accessToken } }
      : {}),
  });
}

/**
 * The capture document's one inline script. It is the same text for every
 * capture, layout and host: what differs is read from the document's
 * `capture-data` block. A host whose Content-Security-Policy cannot carry a
 * nonce (app-web's static policy, apps/mobile/web/contentSecurityPolicy.ts)
 * allows it by its hash; a blob frame inherits its creator's policy. Edit it
 * as one constant, never with interpolation.
 */
export const CAPTURE_SCRIPT = `
const input=JSON.parse(document.getElementById('capture-data').textContent);
const config=input.capture;
const inline=input.inline;
const send=(value)=>{const message=JSON.stringify({type:'lissen-card-capture',captureId:config.id,...value});if(window.ReactNativeWebView)window.ReactNativeWebView.postMessage(message);else parent.postMessage(message,'*')};
try {
const fields=[];
const fieldNames=config.refresh?['cvcAlias']:['panAlias','expiry','cvcAlias'];
let fieldState={};
const form=VGSCollect.create(config.vaultId,config.environment,(state)=>{
  const previous=fieldState;
  // Keep only validation/focus flags, never values or card metadata.
  fieldState=Object.fromEntries(Object.entries(state).map(([name,field])=>[name,{isValid:field.isValid,isFocused:field.isFocused,isTouched:field.isTouched,isEmpty:field.isEmpty}]));
  const row=document.getElementById('card-fields');
  if(!row)return;
  // Only the aggregate validity flag crosses to checkout, never field values.
  send({valid:fieldNames.every(name=>fieldState[name]?.isValid===true),empty:fieldNames.every(name=>fieldState[name]?.isEmpty!==false),focused:Object.values(fieldState).some(field=>field.isFocused)});
  const states=Object.values(fieldState);
  row.classList.toggle('is-focused',states.some(field=>field.isFocused));
  row.classList.toggle('is-invalid',states.some(field=>field.isTouched&&!field.isValid&&!field.isEmpty));
  // Split fields are boxes of their own: each wears its own focus and error.
  for(const [name,id] of [['panAlias','number'],['expiry','expiry'],['cvcAlias','cvc']]){const box=document.getElementById(id);const field=fieldState[name];if(box&&field){box.classList.toggle('is-focused',!!field.isFocused);box.classList.toggle('is-invalid',!!field.isTouched&&!field.isValid&&!field.isEmpty);}}
  const completed=fieldNames.findIndex(name=>fieldState[name]?.isFocused&&fieldState[name]?.isValid&&previous[name]?.isValid===false);
  if(completed<0)return;
  // Advance only from the field being edited. Skip already-complete autofill
  // fields, keep manual focus changes intact, and never submit after CVC.
  const next=fieldNames.findIndex((name,index)=>index>completed&&!fieldState[name]?.isValid);
  if(next>=0)fields[next]?.focus();
});form.setRouteId(config.routeId);
const css={'font-family':input.css.fontFamily,'font-size':input.css.fontSize+'px',color:input.css.text,background:'transparent',border:'none',outline:'none','box-sizing':'border-box',width:'100%',height:'100%',padding:'0',margin:'0','&::placeholder':{color:input.css.placeholder},'&.invalid.touched':{color:input.css.danger}};
if(!config.refresh){fields.push(form.field('#number',{name:'panAlias',type:'card-number',placeholder:'Card number',ariaLabel:'Card number',autoComplete:'cc-number',inputMode:'numeric',showCardIcon:inline?{left:'0',width:'24px',height:'18px'}:false,validations:['required','validCardNumber'],tokenization:{format:'UUID',storage:'PERSISTENT'},css:inline?{...css,paddingLeft:'32px'}:css}));fields.push(form.field('#expiry',{name:'expiry',type:'card-expiration-date',placeholder:'MM/YY',ariaLabel:'Expiry date',autoComplete:'cc-exp',inputMode:'numeric',yearLength:2,validations:['required','validCardExpirationDate'],tokenization:false,css}));}
fields.push(form.field('#cvc',{name:'cvcAlias',type:'card-security-code',placeholder:input.cvcPlaceholder,ariaLabel:'Security code',autoComplete:'cc-csc',inputMode:'numeric',showCardIcon:false,validations:['required','validCardSecurityCode'],tokenization:{format:'UUID',storage:'VOLATILE'},css}));
let submitting=false;
const fail=(message,field)=>{submitting=false;document.getElementById('save').disabled=false;document.getElementById('error').textContent=message;send({error:true,field});};
const validationFailed=()=>{
  if(!inline){fail('Check your card details.');return;}
  const index=fieldNames.findIndex(name=>!fieldState[name]?.isValid);
  const name=fieldNames[index];
  const messages={panAlias:'Check your card number.',expiry:'Check your expiry date.',cvcAlias:'Check your security code.'};
  fail(messages[name]||'Check your card details.',name);
  if(index>=0)fields[index]?.focus();
};
// The capture the aliases are made and confirmed under: this document's own,
// or the fresh one the host renewed it with (captureSubmitMessage).
const submit=(renewed)=>{if(submitting)return;submitting=true;const target=renewed&&typeof renewed.id==='string'&&typeof renewed.accessToken==='string'?renewed:config;const button=document.getElementById('save');button.disabled=true;document.getElementById('error').textContent='';let expired=false;const timeout=setTimeout(()=>{expired=true;fail('Please check your card details and try again.');},15000);form.createAliases({access_token:target.accessToken},(status,data)=>{if(expired)return;clearTimeout(timeout);if(status<200||status>=300){fail('Please check your card details and try again.');return;}const value={captureId:target.id,cvcAlias:data.cvcAlias};if(!config.refresh){const parts=String(data.expiry).split('/').map(s=>s.trim());value.panAlias=data.panAlias;value.expiryMonth=Number(parts[0]);value.expiryYear=2000+Number(parts[1])%100;}submitting=false;button.disabled=false;send({value});},()=>{if(expired)return;clearTimeout(timeout);validationFailed();});};
document.getElementById('form').onsubmit=(event)=>{event.preventDefault();submit();};
if(inline)window.addEventListener('message',(event)=>{if(event.source!==parent||event.origin!==location.origin||typeof event.data!=='string')return;let message;try{message=JSON.parse(event.data);}catch{return;}if(message.type==='lissen-card-submit'&&message.captureId===config.id)submit(message.capture);});
Promise.all(fields.map(field=>field.promise)).then(()=>send(inline?{ready:true,valid:fieldNames.every(name=>fieldState[name]?.isValid===true)}:{ready:true}),()=>fail('Card fields could not load. Please try again.'));
}catch{document.getElementById('error').textContent='Card fields could not load. Please try again.';send({error:true});}
`;

/** Identical hosted card fields on web and native WebView. Raw PAN/CVC stay in
 * cross-origin VGS iframes; this document only receives opaque aliases. */
export function captureHtml(
  capture: CardCapture,
  options: {
    inline?: boolean;
    appearance?: Partial<CaptureAppearance>;
    scriptNonce?: string;
  } = {},
) {
  // Blob frames inherit their creator's CSP. Carry its nonce onto only our
  // two scripts rather than requiring unsafe-inline on the checkout.
  const nonce =
    options.scriptNonce && /^[A-Za-z0-9+/_=-]+$/.test(options.scriptNonce)
      ? ` nonce="${options.scriptNonce}"`
      : "";
  const inline = !!options.inline;
  const a = captureAppearance(options.appearance);
  const split = inline && a.layout === "split";
  // What differs between captures travels as data, so the one script below
  // never changes and a static policy can allow it by hash (CAPTURE_SCRIPT).
  // \u003c keeps a value from closing the data block.
  const data = JSON.stringify({
    capture,
    inline,
    css: inline
      ? {
          fontFamily: a.fontFamily,
          fontSize: a.fontSize,
          text: a.text,
          placeholder: a.placeholder,
          danger: a.danger,
        }
      : {
          fontFamily: DEFAULT_CAPTURE_APPEARANCE.fontFamily,
          fontSize: 16,
          text: "#17213b",
          placeholder: "#657089",
          danger: "#9a2525",
        },
    cvcPlaceholder: capture.refresh && !inline ? "Security code" : "CVC",
  }).replace(/</g, "\\u003c");
  // VGS's single-line form groups its isolated fields in one shared control;
  // the split form gives each isolated field its own box. Either way each
  // field stays hosted by VGS and the alias contract is unchanged.
  const fields = split
    ? `<div id="card-fields" class="card-split" role="group" aria-label="${capture.refresh ? "Card security code" : "Card information"}">${capture.refresh ? '<div id="cvc" class="field"></div>' : '<div id="number" class="field"></div><div class="pair"><div id="expiry" class="field"></div><div id="cvc" class="field"></div></div>'}</div>`
    : inline
      ? `<div id="card-fields" class="card-fields${capture.refresh ? " card-fields-refresh" : ""}" role="group" aria-label="${capture.refresh ? "Card security code" : "Card information"}">${capture.refresh ? "" : '<div id="number" class="field"></div><div id="expiry" class="field"></div>'}<div id="cvc" class="field"></div></div>`
      : `${capture.refresh ? "" : '<label for="number">Card number</label><div id="number" class="field"></div>'}<div class="row">${capture.refresh ? "" : '<div><label for="expiry">Expiry date</label><div id="expiry" class="field"></div></div>'}<div><label for="cvc">Security code</label><div id="cvc" class="field"></div></div></div>`;
  return `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><style>
*{box-sizing:border-box}.field{min-width:0;overflow:hidden}.field iframe{display:block;width:100%;height:100%;border:0;background:transparent;color-scheme:normal}
body{font:16px system-ui;margin:0;padding:16px;color:#17213b;background:white;box-sizing:border-box}label{display:block;font-size:14px;margin:0 0 8px}.field{height:46px;border:1px solid #bdc4d3;border-radius:8px;margin-bottom:16px;padding:0 12px}button{width:100%;border:0;border-radius:8px;padding:12px;background:#263b70;color:white;font:inherit}button:disabled{opacity:.5}p{font-size:13px;line-height:1.4}#error{color:#9a2525}
${inline ? `html,body{width:100%;margin:0;padding:0;overflow:hidden;color:${a.text};background:transparent;color-scheme:normal}form{margin:0}#save,#error{display:none}` : ""}${
    split
      ? `.card-split{display:grid;gap:${a.gap}px;width:100%;padding:0}.card-split .pair{display:grid;grid-template-columns:1fr 1fr;gap:${a.gap}px}.card-split .field{height:${a.height}px;margin:0;padding:0 12px;border:1px solid ${a.border};border-radius:${a.radius}px;background:${a.background};transition:border-color .15s,box-shadow .15s}.card-split .field.is-focused{border-color:${a.focusBorder};box-shadow:${a.focusShadow}}.card-split .field.is-invalid{border-color:${a.danger}}@media(prefers-reduced-motion:reduce){.card-split .field{transition:none}}${a.numberTrailingPadding > 0 ? `.card-split #number{padding-right:${12 + a.numberTrailingPadding}px}` : ""}`
      : inline
        ? `.card-fields{display:grid;grid-template-columns:minmax(0,1fr) 72px 48px;gap:${a.gap}px;align-items:center;height:${a.height}px;width:100%;padding:0 12px;border:1px solid ${a.border};border-radius:${a.radius}px;background:${a.background};transition:border-color .15s,box-shadow .15s}.card-fields-refresh{grid-template-columns:minmax(0,1fr)}.card-fields.is-focused{border-color:${a.focusBorder};box-shadow:${a.focusShadow}}.card-fields.is-invalid{border-color:${a.danger}}.card-fields .field{height:${a.height - 2}px;border:0;border-radius:0;margin:0;padding:0;background:transparent}@media(prefers-reduced-motion:reduce){.card-fields{transition:none}}`
        : ""
  }</style>
<script${nonce} src="${VGS_COLLECT_SCRIPT_URL}" integrity="${VGS_COLLECT_SCRIPT_INTEGRITY}" crossorigin="anonymous"></script></head><body>
<form id="form">${fields}
<button id="save" type="submit">${capture.refresh ? "Confirm security code" : "Save card to Lissen"}</button><p id="error" role="alert"></p></form>
<script type="application/json" id="capture-data">${data}</script>
<script${nonce}>${CAPTURE_SCRIPT}</script></body></html>`;
}
